import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GlobalVariablesHelper } from '@shared/helpers';
import { ExcelExportConfig, ExcelHeaderStyle, UserOrgInfo } from '@shared/types';
import { TableColumn } from '@ui-kit/table/_core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import autoTable, { RowInput } from 'jspdf-autotable';

const DEFAULT_COLUMN_WIDTH = 10;
const DEFAULT_HEADER_FONT_COLOR = 'FFFFFFFF';
const DEFAULT_HEADER_BACKGROUND_COLOR = 'FF2980BA';

@Injectable({
    providedIn: 'root',
})
export class ExportService {
    private translateService = inject(TranslateService);

    exportPdf(columnNames: string[], body: RowInput[], columnStyles: any, pdfTitle: string, pdfName: string) {
        import('jspdf').then(jsPDF => {
            import('jspdf-autotable').then(() => {
                import('jspdf-autotable').then(() => {
                    const doc = new jsPDF.default('landscape');
                    doc.text(pdfTitle, 8, 16);

                    autoTable(doc, {
                        margin: { top: 25, left: 8 },
                        head: [columnNames],
                        body: body,
                        columnStyles: columnStyles,
                        didDrawPage: data => {
                            data.settings.margin.top = 10;
                        },
                    });

                    doc.save(pdfName + '.pdf');
                });
            });
        });
    }

    async exportExcel(rows: (string | number)[][], columnStyles: number[], fileName: string): Promise<void> {
        const config: ExcelExportConfig = {
            rows,
            columnWidths: columnStyles,
            fileName,
        };

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(config.sheetName || config.fileName);

        this.setColumnWidths(worksheet, config.columnWidths);
        this.addRowsWithStyling(worksheet, config.rows, config.headerStyle);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `${fileName}.xlsx`);
    }

    async exportExcelMultiSheet(
        sheets: Array<{
            sheetName: string;
            rows: (string | number | ExcelJS.CellRichTextValue)[][];
            columnWidths: number[];
            headerStyle?: ExcelHeaderStyle;
            isDefaultSheet?: boolean;
        }>,
        fileName: string
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();

        sheets.forEach(sheet => {
            const worksheet = workbook.addWorksheet(sheet.sheetName);
            if (sheet.isDefaultSheet) {
                const id = workbook.worksheets.filter(s => s.name === sheet.sheetName)[0]?.id;
                this.setDefaultSheet(workbook, id);
            }
            this.setColumnWidths(worksheet, sheet.columnWidths);
            this.addRowsWithStyling(worksheet, sheet.rows, sheet.headerStyle);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `${fileName}.xlsx`);
    }

    private setDefaultSheet(workbook: ExcelJS.Workbook, id: number | undefined): void {
        if (!id) {
            return;
        }

        workbook.views = [
            {
                x: 0, // default value
                y: 0, // default value
                width: 10000, // default value
                height: 20000, // default value
                firstSheet: 0, // default value
                activeTab: id - 1,
                visibility: 'visible', // default value
            },
        ];
    }

    private setColumnWidths(worksheet: ExcelJS.Worksheet, columnWidths: number[]): void {
        worksheet.columns = columnWidths.map((col: number) => ({
            width: col || DEFAULT_COLUMN_WIDTH,
        }));
    }

    private addRowsWithStyling(
        worksheet: ExcelJS.Worksheet,
        rows: (string | number | ExcelJS.CellRichTextValue)[][],
        headerStyle?: ExcelHeaderStyle
    ): void {
        rows.forEach((row: (string | number | ExcelJS.CellRichTextValue)[], rowIndex: number) => {
            const rowValues = row.map(cellValue => {
                if (cellValue && typeof cellValue === 'object' && 'richText' in cellValue) {
                    return '';
                }
                return cellValue;
            });
            const excelRow = worksheet.addRow(rowValues);

            row.forEach((cellValue: string | number | ExcelJS.CellRichTextValue, colIndex: number) => {
                const excelCell = excelRow.getCell(colIndex + 1);
                this.applyDefaultCellAlignment(excelCell);

                if (cellValue && typeof cellValue === 'object' && 'richText' in cellValue) {
                    excelCell.value = cellValue as ExcelJS.CellRichTextValue;
                }

                if (rowIndex === 0) {
                    this.applyHeaderCellStyle(excelCell, headerStyle);
                }
            });
        });
    }

    private applyDefaultCellAlignment(cell: ExcelJS.Cell): void {
        cell.alignment = {
            ...cell.alignment,
            wrapText: true,
            vertical: 'top',
        };
    }

    private applyHeaderCellStyle(cell: ExcelJS.Cell, headerStyle?: ExcelHeaderStyle): void {
        const style = headerStyle || {};

        cell.font = {
            ...cell.font,
            bold: style.bold !== false,
            color: { argb: style.fontColor || DEFAULT_HEADER_FONT_COLOR },
        };

        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: style.backgroundColor || DEFAULT_HEADER_BACKGROUND_COLOR },
        };

        if (style.alignment) {
            cell.alignment = {
                ...cell.alignment,
                ...style.alignment,
            };
        }
    }

    getColumnWidthOnPdf(columns: TableColumn<any>[]) {
        const columnStyles: any = {};

        const totalRatio = this.getTotalRatioForPdf(columns);
        const mediumRatio = GlobalVariablesHelper.pdfPageWidthOnLandscape / totalRatio;

        columns.forEach((column: TableColumn<any>, index: number) => {
            columnStyles[index] = { cellWidth: mediumRatio * column.widthRatio };
        });

        return columnStyles;
    }

    getColumnNames<T extends string | number | symbol>(
        visibleColumns: TableColumn<T>[],
        columnsNameText: Record<T, string>
    ): string[] {
        const columnNames: string[] = [];
        visibleColumns.map((column: TableColumn<T>) => {
            const r = this.translateService.instant(columnsNameText[column.field]);
            if (r) {
                columnNames.push(r);
            }
        });

        return columnNames;
    }

    mapListToExportExcel(columns: string[], rowData: (string | number)[][]): (string | number)[][] {
        return [columns, ...rowData];
    }

    getColumnWidthOnExcel(visibleColumns: TableColumn<any>[]): number[] {
        const mediumRatio = DEFAULT_COLUMN_WIDTH;
        return visibleColumns.map((column: TableColumn<any>) => mediumRatio * column.widthRatio);
    }

    insertRow(data: any[]): (string | number)[][] {
        return data.map((item: any) => {
            return Object.keys(item).map(key => item[key]);
        });
    }

    computeUserCell(requester: UserOrgInfo | null): string {
        if (!requester) {
            return '-';
        }

        return `${requester?.fullName}\n${requester?.organizationName}\n${requester.email}`;
    }

    private getTotalRatioForPdf(columns: TableColumn<any>[]): number {
        let totalRatio = 0;
        columns.forEach(col => {
            totalRatio += col.widthRatio;
        });

        return totalRatio;
    }
}
